import React, { useEffect, useMemo, useRef, useState } from "react";

import "./rangeSlider.scss";
import { clamp } from "lodash";

type TSingleRange = {
  type: "single";
  value: number;
  valueLabel: string;
  setValue: (value: number) => void;
};

type TDualRange = {
  type: "dual";
  start: number;
  startLabel: string;
  end: number;
  endLabel: string;
  setStart: (start: number) => void;
  setEnd: (end: number) => void;
};

type TRangeSliderProps = {
  range: TSingleRange | TDualRange;
  min: number;
  max: number;
  step: number;
};

interface IRangeSliderState {
  draggingSingleHandle: boolean;
  draggingStartHandle: boolean;
  draggingEndHandle: boolean;
  sliderWidth: number;
  stepSliderAdjustments: number[];
}

export default function RangeSlider(props: TRangeSliderProps) {
  const sliderRef = useRef<HTMLDivElement>(null);
  const startSliderHandleRef = useRef<HTMLDivElement>(null);
  const endSliderHandleRef = useRef<HTMLDivElement>(null);

  const [state, setState] = useState<IRangeSliderState>({
    draggingSingleHandle: false,
    draggingStartHandle: false,
    draggingEndHandle: false,
    sliderWidth: null,
    stepSliderAdjustments: null,
  });

  useEffect(() => {
    if (!sliderRef.current) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      const updatedSliderWidth = sliderRef.current?.getBoundingClientRect()?.width;

      if (updatedSliderWidth == null) {
        setState(prev => ({ ...prev, sliderWidth: null, stepSliderAdjustments: null }));
      } else {
        const updatedStepSliderAdjustments: number[] = [];

        const stepPercent = props.step / (props.max - props.min);

        const adjustmentStep = stepPercent * updatedSliderWidth;

        let adjustment = 0;
        let adjustmentIndex = 0;

        while (updatedSliderWidth - adjustment > 0.01) {
          adjustment = adjustmentIndex * adjustmentStep;
          updatedStepSliderAdjustments.push(adjustment);
          adjustmentIndex++;
        }

        setState(prev => ({
          ...prev,
          sliderWidth: updatedSliderWidth,
          stepSliderAdjustments: updatedStepSliderAdjustments,
        }));
      }
    });

    resizeObserver.observe(sliderRef.current);
    return () => resizeObserver.disconnect();
  }, [sliderRef.current, props.min, props.max, props.step]);

  const { singleSliderAdjustment, startSliderAdjustment, endSliderAdjustment } = useMemo(() => {
    const adjustments = {
      singleSliderAdjustment: 0,
      startSliderAdjustment: 0,
      endSliderAdjustment: 0,
    };

    if (state.sliderWidth != null) {
      if (props.range?.type === "single" && props.range.value != null) {
        const valuePercent = (props.range.value - props.min) / (props.max - props.min);
        adjustments.singleSliderAdjustment = valuePercent * state.sliderWidth;
      }

      if (props.range?.type === "dual") {
        if (props.range.start != null) {
          const startPercent = (props.range.start - props.min) / (props.max - props.min);
          adjustments.startSliderAdjustment = startPercent * state.sliderWidth;
        }
        if (props.range.end != null) {
          const endPercent = (props.range.end - props.min) / (props.max - props.min);
          adjustments.endSliderAdjustment = endPercent * state.sliderWidth;
        }
      }
    }

    return adjustments;
  }, [
    props.range?.type,
    props.range?.type === "single" ? props.range.value : undefined,
    props.range?.type === "dual" ? props.range.start : undefined,
    props.range?.type === "dual" ? props.range.end : undefined,
    state.sliderWidth,
    props.min,
    props.max,
  ]);

  function handleDragRelease(e: MouseEvent | TouchEvent) {
    if (state.draggingSingleHandle) {
      setState(prev => ({ ...prev, draggingSingleHandle: false }));
    } else if (state.draggingStartHandle) {
      setState(prev => ({ ...prev, draggingStartHandle: false }));
    } else if (state.draggingEndHandle) {
      setState(prev => ({ ...prev, draggingEndHandle: false }));
    }
  }

  useEffect(() => {
    document.addEventListener("mouseup", handleDragRelease);
    document.addEventListener("touchend", handleDragRelease);
    document.addEventListener("touchcancel", handleDragRelease);

    return () => {
      document.removeEventListener("mouseup", handleDragRelease);
      document.removeEventListener("touchend", handleDragRelease);
      document.removeEventListener("touchcancel", handleDragRelease);
    };
  }, [state.draggingSingleHandle, state.draggingStartHandle, state.draggingEndHandle]);

  function handleDrag(e: MouseEvent | TouchEvent) {
    let clientX: number = null;

    if (e instanceof MouseEvent) {
      clientX = e.clientX;
    } else if (e instanceof TouchEvent) {
      clientX = e.touches?.[0]?.clientX;
    }

    if (
      clientX == null ||
      state.sliderWidth == null ||
      state.stepSliderAdjustments == null ||
      state.stepSliderAdjustments.length === 0
    ) {
      return;
    } else if (state.draggingSingleHandle && props.range?.type === "single") {
      const sliderX = sliderRef.current.getBoundingClientRect().x;
      const smoothSliderAdjustment = clientX - sliderX;

      let lowestDelta: number = null;
      let lowestDeltaIndex: number = null;

      for (let i = 0; i < state.stepSliderAdjustments.length; i++) {
        const delta = Math.abs(state.stepSliderAdjustments[i] - smoothSliderAdjustment);

        if (lowestDelta == null || delta < lowestDelta) {
          lowestDelta = delta;
          lowestDeltaIndex = i;
        }
      }

      const updatedValue = clamp(props.min + lowestDeltaIndex * props.step, props.min, props.max);

      props.range.setValue(updatedValue);
    } else if (state.draggingStartHandle && props.range?.type === "dual") {
      const sliderX = sliderRef.current.getBoundingClientRect().x;
      const smoothSliderAdjustment = clientX - sliderX;

      let lowestDelta: number = null;
      let lowestDeltaIndex: number = null;

      for (let i = 0; i < state.stepSliderAdjustments.length; i++) {
        const adjustmentInBounds =
          state.stepSliderAdjustments[i] + startSliderHandleRef.current.getBoundingClientRect().width / 2 <
          endSliderAdjustment - endSliderHandleRef.current.getBoundingClientRect().width / 2;
        const delta = Math.abs(state.stepSliderAdjustments[i] - smoothSliderAdjustment);

        if (adjustmentInBounds && (lowestDelta == null || delta < lowestDelta)) {
          lowestDelta = delta;
          lowestDeltaIndex = i;
        }
      }

      const updatedStart = clamp(props.min + lowestDeltaIndex * props.step, props.min, props.max);

      props.range.setStart(updatedStart);
    } else if (state.draggingEndHandle && props.range?.type === "dual") {
      const sliderX = sliderRef.current.getBoundingClientRect().x;
      const smoothSliderAdjustment = clientX - sliderX;

      let lowestDelta: number = null;
      let lowestDeltaIndex: number = null;

      for (let i = 0; i < state.stepSliderAdjustments.length; i++) {
        const adjustmentInBounds =
          state.stepSliderAdjustments[i] - endSliderHandleRef.current.getBoundingClientRect().width / 2 >
          startSliderAdjustment + startSliderHandleRef.current.getBoundingClientRect().width / 2;
        const delta = Math.abs(state.stepSliderAdjustments[i] - smoothSliderAdjustment);

        if (adjustmentInBounds && (lowestDelta == null || delta < lowestDelta)) {
          lowestDelta = delta;
          lowestDeltaIndex = i;
        }
      }

      const updatedEnd = clamp(props.min + lowestDeltaIndex * props.step, props.min, props.max);

      props.range.setEnd(updatedEnd);
    }
  }

  useEffect(() => {
    document.addEventListener("mousemove", handleDrag);
    document.addEventListener("touchmove", handleDrag);

    return () => {
      document.removeEventListener("mousemove", handleDrag);
      document.removeEventListener("touchmove", handleDrag);
    };
  }, [
    state.draggingSingleHandle,
    state.draggingStartHandle,
    state.draggingEndHandle,
    state.sliderWidth,
    state.stepSliderAdjustments,
    startSliderAdjustment,
    endSliderAdjustment,
    props.range?.type,
    props.min,
    props.max,
    props.step,
    sliderRef.current,
    startSliderHandleRef.current,
    endSliderHandleRef.current,
  ]);

  function calculateSliderLinearGradient() {
    if (props.range?.type === "dual") {
      const startPercent = (startSliderAdjustment / state.sliderWidth) * 100;
      const endPercent = (endSliderAdjustment / state.sliderWidth) * 100;

      return `linear-gradient(
        to right,
        grey 0%,
        grey ${startPercent}%,
        #D1D1D1 ${startPercent}%,
        #D1D1D1 ${endPercent}%,
        grey ${endPercent}%,
        grey 100%
      )`;
    } else {
      return null;
    }
  }

  return (
    <div className="range-slider-container">
      <div
        ref={sliderRef}
        className="range-slider"
        style={{
          background: calculateSliderLinearGradient(),
        }}
      >
        {props.range.type === "single" && (
          <div
            className="range-slider-handle"
            style={{
              left: `${singleSliderAdjustment}px`,
            }}
            onMouseDown={e => {
              if (e.button === 0) {
                setState(prev => ({ ...prev, draggingSingleHandle: true }));
              }
            }}
            onTouchStart={e => {
              setState(prev => ({ ...prev, draggingSingleHandle: true }));
            }}
            // Disabled dragging
            draggable="true"
            onDragStart={e => {
              e.preventDefault();
              e.stopPropagation();
            }}
          />
        )}
        {props.range.type === "dual" && (
          <>
            <div
              ref={startSliderHandleRef}
              className="range-slider-handle"
              style={{
                left: `${startSliderAdjustment}px`,
              }}
              onMouseDown={e => {
                if (e.button === 0) {
                  setState(prev => ({ ...prev, draggingStartHandle: true }));
                }
              }}
              onTouchStart={e => {
                setState(prev => ({ ...prev, draggingStartHandle: true }));
              }}
              // Disabled dragging
              draggable="true"
              onDragStart={e => {
                e.preventDefault();
                e.stopPropagation();
              }}
            />
            <div
              ref={endSliderHandleRef}
              className="range-slider-handle"
              style={{
                left: `${endSliderAdjustment}px`,
              }}
              onMouseDown={e => {
                if (e.button === 0) {
                  setState(prev => ({ ...prev, draggingEndHandle: true }));
                }
              }}
              onTouchStart={e => {
                setState(prev => ({ ...prev, draggingEndHandle: true }));
              }}
              // Disabled dragging
              draggable="true"
              onDragStart={e => {
                e.preventDefault();
                e.stopPropagation();
              }}
            />
          </>
        )}
      </div>
      {props.range.type === "single" && (
        <div className="range-slider-labels-container">
          <div>{props.range.valueLabel}</div>
        </div>
      )}
      {props.range.type === "dual" && (
        <div className="range-slider-labels-container">
          <div>{props.range.startLabel}</div>–<div>{props.range.endLabel}</div>
        </div>
      )}
    </div>
  );
}
